import {
  ComponentPropsWithRef,
  createContext,
  ElementType,
  ReactNode,
  useContext,
} from 'react';

import { styled, useTheme } from '@mui/material';
import ButtonBase, { ButtonBaseProps } from '@mui/material/ButtonBase';
import LinearProgress from '@mui/material/LinearProgress';

import { Sizes, WithRef } from '../../types';
import { IconContext } from '../icons';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'text';

export type ButtonSize = Sizes<'sm' | 'md' | 'lg'>;

export type ButtonProps<C extends ElementType = 'button'> = ButtonBaseProps<
  C,
  {
    component?: C;
    loading?: boolean;
    variant?: ButtonVariant;
    size?: ButtonSize;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
    fullWidth?: boolean;
    testId?: string;
  }
>;

export const ButtonContext = createContext<ButtonProps | undefined>(undefined);

const ButtonRoot = styled(
  <C extends ElementType = 'button'>({
    ref,
    children,
    loading,
    disabled,
    startIcon,
    endIcon,
    ...props
  }: WithRef<HTMLButtonElement, ButtonProps<C>>) => {
    return (
      <IconContext.Provider
        value={{
          size: '1em',
          weight: props.size === 'sm' ? 'regular' : 'bold',
        }}
      >
        <ButtonBase {...props} disabled={disabled || loading} ref={ref}>
          {startIcon && <span className="DsButton-StartIcon">{startIcon}</span>}
          {children}
          {endIcon && <span className="DsButton-EndIcon">{endIcon}</span>}
          {loading && <LinearProgress />}
        </ButtonBase>
      </IconContext.Provider>
    );
  },
  {
    name: 'DsButton',
    slot: 'Root',
    target: 'DsButton-root',
    shouldForwardProp: (prop) => prop !== 'fullWidth',
    overridesResolver: (
      { variant, size, disabled, loading, startIcon, endIcon },
      theme
    ) => {
      return [
        theme.root,
        theme[variant],
        theme[size],
        startIcon ? theme.startIcon : {},
        endIcon ? theme.endIcon : {},
        disabled || loading ? theme.disabled : {},
        loading ? theme.loading : {},
      ];
    },
  }
)({});

const Button = (({
  ref,
  testId,
  ...props
}: WithRef<HTMLButtonElement, ButtonProps>) => {
  const buttonContext = useContext(ButtonContext);
  const theme = useTheme();

  const _props = {
    ...(theme.components?.DsButton?.defaultProps ?? {}),
    ...buttonContext,
    ...props,
  };

  return <ButtonRoot data-testid={testId} {..._props} ref={ref} />;
}) as (<C extends ElementType = 'button'>(
  props: ButtonProps<C> & {
    ref?: ComponentPropsWithRef<C>['ref'];
  }
) => React.ReactElement) & {
  displayName?: string;
};

Button.displayName = 'Button';

export default Button;
